/* The empty card's block — see `EmptyState.tsx` for the three tones and why they are not one.
 *
 * Nothing here declares a height. The card around it owns that, through its own reserve, and this
 * block centres itself in whatever it is given: the entire value of a reserve is that the short
 * state and the loaded state stand in one box, and an empty state that grew its own card would
 * trade a copy defect for the layout defect the reserves exist to prevent. */

.empty {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  align-items: center;
  justify-content: center;
  text-align: center;
}

.mark {
  display: block;
  width: var(--sz-empty-mark);
  height: var(--sz-empty-mark);
}

/* The sentence, at the same size, weight and ink every short state on this screen already used —
 * so what the drawing adds is a drawing, and not a second typographic voice. */
.title {
  max-width: 34ch;
  margin: 0;
  font-size: var(--fs-note);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

/* `--text-2`, raised from `--text-3` on 16 September 2026 and app-wide rather than on one card.
 * The hint is the line that says where the missing thing is made, or — on the queue cards — the
 * sentence that is the reward for an empty morning, and `docs/design/README.md` states the rule
 * without an exception: "`--text-3` carries no text that must be read". MEASURED on the queue
 * card's own ground before the change: 3.01:1 in Flame, under AA at the 11px it is set at. */
.hint {
  max-width: 38ch;
  margin: 0;
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

/* ── The inline variant ───────────────────────────────────────────────────────
 * A sentence INSIDE a card that is drawn rather than instead of one. The mark rides on the
 * sentence's own line at cap height, so the card does not gain a row — which matters here: the
 * summary column already fails its no-scroll fit by 39px on the card this variant appears in.
 *
 * The INLINE variant declares layout and nothing else — see `EmptyState.tsx`. Its `margin: 0` is
 * the one declaration a caller's own class also carries, and every one of them zeroes it too, so
 * which stylesheet's chunk comes first cannot change the render. Nothing else here may be
 * duplicated on the caller's side for that reason.
 *
 * `align-items: center` rather than `baseline`, and the difference is measurable: a 16px block
 * baselines on its BOTTOM edge, which pushed the line box 2px taller than the same sentence
 * without a mark — and on the note card that 2px moved the three cards under it. Centred, the row
 * is the taller of the text's line box and the mark, and on every surface here the text wins. */
.inline {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  margin: 0;
}

.inlineMark {
  display: block;
  flex: none;
  width: var(--sz-empty-mark-inline);
  height: var(--sz-empty-mark-inline);
}

/* ── The three tones ──────────────────────────────────────────────────────────
 * A SHAPE in the tone's ink, never a fill and never a blur. `--good-ink` is `--good`'s own hue
 * moved per theme (in Flame Dark the two tokens hold the same value), the treatment this app
 * already applies to `--lime-ink`. */
.tonePositive {
  color: var(--good-ink);
}

.toneAction {
  color: var(--text-2);
}

.toneNeutral {
  color: var(--text-3);
}

/* ── The page variant ─────────────────────────────────────────────────────────
 * A state that IS the screen (the empty Zadania queue, owner's E2, 5 November 2026). The sentence
 * reads as the screen's heading and the hint as plain text under it: before this, a title in
 * medium `--fs-note` over a semibold hint read lighter than its own footnote. The mark sits on a
 * neutral chip so it holds the centre of an otherwise empty desk. Lines wrap balanced, so a hint
 * never leaves one word alone on its last line. */
.page {
  gap: var(--space-10px);
}

.page .mark {
  box-sizing: content-box;
  width: var(--sz-empty-mark-page);
  height: var(--sz-empty-mark-page);
  margin-bottom: var(--space-6px);
  padding: var(--space-14px);
  background: var(--bg-chip);
  border-radius: var(--r-circle);
}

.page .title {
  max-width: 40ch;
  font-size: var(--type-title-3);
  font-weight: var(--fw-semibold);
  line-height: var(--type-title-3-lh);
  color: var(--text);
  text-wrap: balance;
}

.page .hint {
  max-width: 46ch;
  font-size: var(--type-control);
  font-weight: var(--fw-regular);
  line-height: var(--type-body-lh);
  color: var(--text-2);
  text-wrap: balance;
}
